<template>
	<div>
		<div class="d-flex align-center px-4 pt-4 pb-2">
			<span class="text-body-2 text-grey-darken-2">
				{{ pluralize("measurement", measurements.length, true) }}
			</span>
			<v-spacer />
			<v-btn
				v-if="canManage"
				class="bg-secondary"
				size="small"
				elevation="2"
				prepend-icon="mdi-plus"
				@click="$emit('create')"
			>
				New measurement
			</v-btn>
		</div>

		<v-expansion-panels
			v-if="measurements.length > 0"
			v-model="openPanelId"
			variant="accordion"
		>
			<lazy-render
				v-for="measurement in measurements"
				:key="measurement.id"
				class="measurement-lazy-row"
				:placeholder-height="72"
				:force-render="measurement.id === openPanelId"
			>
				<v-expansion-panel :value="measurement.id">
					<v-expansion-panel-title>
						<span class="font-weight-bold flex-grow-1">
							{{ measurement.name }}
						</span>
						<v-chip
							v-if="measurement.preferredUnit"
							size="small"
							variant="flat"
							class="mr-2 my-n1 flex-shrink-0 bg-provenance-b-tint text-provenance-b-ink"
						>
							<v-icon start size="20">mdi-ruler</v-icon>
							{{ measurement.preferredUnit.ucumCode }}
						</v-chip>
						<v-chip
							v-if="measurement.molarMass !== null"
							size="small"
							color="teal-darken-1"
							variant="tonal"
							class="mr-2 my-n1 flex-shrink-0"
						>
							{{ measurement.molarMass }} g/mol
						</v-chip>
						<v-chip
							v-if="measurement.aliasCount > 0"
							size="small"
							color="grey-darken-1"
							variant="tonal"
							class="mr-2 my-n1 flex-shrink-0"
						>
							{{ pluralize("alias", measurement.aliasCount, true) }}
						</v-chip>
						<v-btn
							v-if="canManage"
							size="small"
							variant="text"
							color="grey-darken-1"
							icon="mdi-pencil"
							class="mr-1"
							:aria-label="'Edit ' + measurement.name"
							@click.stop="$emit('edit', measurement)"
						/>
						<v-btn
							v-if="canManage"
							size="small"
							variant="text"
							color="grey-darken-1"
							icon="mdi-delete"
							class="mr-2"
							:aria-label="'Delete ' + measurement.name"
							@click.stop="confirmDelete(measurement)"
						/>
					</v-expansion-panel-title>
					<v-expansion-panel-text>
						<div class="text-body-2">
							<div class="mb-1">
								<span class="font-weight-medium">
									Preferred unit:
								</span>
								<template v-if="measurement.preferredUnit">
									{{ measurement.preferredUnit.label }} ({{
										measurement.preferredUnit.ucumCode
									}})
									<span
										v-if="measurement.preferredUnit.dimension"
										class="text-grey-darken-2"
									>
										-
										{{ measurement.preferredUnit.dimension }}
									</span>
								</template>
								<span v-else class="text-grey-darken-2">
									not set
								</span>
							</div>
							<div class="mb-1">
								<span class="font-weight-medium">
									Molar mass:
								</span>
								<template v-if="measurement.molarMass !== null">
									{{ measurement.molarMass }} g/mol
								</template>
								<span v-else class="text-grey-darken-2">
									not set
								</span>
							</div>
							<div>
								<span class="font-weight-medium">Aliases:</span>
								<template
									v-if="
										measurement.aliases &&
										measurement.aliases.length > 0
									"
								>
									<v-chip
										v-for="alias in measurement.aliases"
										:key="alias"
										size="x-small"
										variant="outlined"
										class="ma-1"
									>
										{{ alias }}
									</v-chip>
								</template>
								<span v-else class="text-grey-darken-2">
									none
								</span>
							</div>
						</div>
					</v-expansion-panel-text>
				</v-expansion-panel>
			</lazy-render>
		</v-expansion-panels>

		<v-list v-else>
			<v-list-item>
				<template #subtitle>
					<p class="text-body-2">
						No preferred units defined yet.
						<template v-if="canManage">
							Add one with New measurement.
						</template>
					</p>
				</template>
			</v-list-item>
		</v-list>

		<v-dialog v-model="deleteDialog" max-width="440px">
			<v-card>
				<v-card-title class="text-body-1">
					Delete measurement?
				</v-card-title>
				<v-card-text class="text-body-2">
					This soft-deletes
					<strong>{{ measurementToDelete?.name }}</strong>
					and its preferred-unit standard. The recognized-unit registry
					is not affected.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn variant="text" @click="deleteDialog = false">
						Cancel
					</v-btn>
					<v-btn
						variant="flat"
						class="bg-valence-negative-tint text-valence-negative-ink"
						@click="performDelete"
					>
						Delete
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import { mapActions } from "pinia";
import { useMainStore } from "@/store";
import LazyRender from "@/pages/QualityControl/LazyRender.vue";
import pluralize from "@/utils/pluralize";

export default {
	name: "PreferredUnitList",
	components: { LazyRender },
	props: {
		measurements: { type: Array, default: () => [] },
		canManage: { type: Boolean, default: false },
	},
	emits: ["edit", "create", "changed"],
	data() {
		return {
			openPanelId: null,
			deleteDialog: false,
			measurementToDelete: null,
		};
	},
	methods: {
		pluralize,

		...mapActions(useMainStore, ["showNotification"]),
		confirmDelete(measurement) {
			this.measurementToDelete = measurement;
			this.deleteDialog = true;
		},
		performDelete() {
			const measurement = this.measurementToDelete;
			if (!measurement) return;
			this.$http
				.post("/dactyl-units/delete", { id: measurement.id })
				.then(() => {
					this.showNotification({
						status: "success",
						message: "Successfully deleted preferred unit",
					});
					this.deleteDialog = false;
					this.measurementToDelete = null;
					this.$emit("changed");
				})
				.catch((error) => {
					const message =
						error?.response?.data?.message ??
						"A problem occurred while deleting the preferred unit";
					this.showNotification({ status: "error", message });
					this.deleteDialog = false;
				});
		},
	},
};
</script>

<style scoped>
/* LazyRender wraps each panel; make the wrapper span the full row width so the
   expansion panels lay out as if they were direct children. */
.measurement-lazy-row {
	flex: 1 0 100%;
	max-width: 100%;
}
</style>
